/** Display formats for the Operations Hub: numbers always with their units (brand book). */
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const parts = (iso: string): { y: number; m: number; d: number } => {
  const [y, m, d] = iso.split('-').map(Number);
  return { y: y!, m: m!, d: d! };
};

/** "12 Oct". */
export function dayLabel(iso: string): string {
  const { m, d } = parts(iso);
  return `${d} ${MONTHS[m - 1]}`;
}

/** "Mon 12 Oct 2026". */
export function longDay(iso: string): string {
  const { y, m, d } = parts(iso);
  return `${WEEKDAYS[new Date(Date.UTC(y, m - 1, d)).getUTCDay()]} ${d} ${MONTHS[m - 1]} ${y}`;
}

/** "12 Oct 2026". */
export function dateText(iso: string): string {
  const { y, m, d } = parts(iso);
  return `${d} ${MONTHS[m - 1]} ${y}`;
}

const one = (v: number): string =>
  v.toLocaleString('en-AU', { minimumFractionDigits: 1, maximumFractionDigits: 1 });

/** "67.5 h". */
export const hoursText = (h: number): string => `${one(h)} h`;

/** "0.5 days", "1 day". */
export function daysText(d: number): string {
  const v = d.toLocaleString('en-AU', { maximumFractionDigits: 2 });
  return `${v} ${d === 1 ? 'day' : 'days'}`;
}

/** "13,311 L". */
export const litresText = (l: number): string => `${Math.round(l).toLocaleString('en-AU')} L`;

/** "191.62 km/h", or an en dash without survey hours. */
export const kmPerHourText = (v: number | null): string =>
  v === null
    ? '–'
    : `${v.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} km/h`;

/**
 * "Flew on 9 of 9 project days" (decision 228; R52 names the available days project days); an en dash when the
 * aircraft had no project days.
 */
export function utilisationText(
  flying: number,
  projectDays: number,
  unit: 'project' | 'aircraft' = 'project',
): string {
  if (projectDays <= 0) return '–';
  const word = unit === 'aircraft' ? 'aircraft-day' : 'project day';
  return `Flew on ${flying} of ${projectDays} ${word}${projectDays === 1 ? '' : 's'}`;
}

/** What the Aircraft and projects columns mean (R52), for their hover text; the caption says the same. */
export const AIRCRAFT_COLUMN_HINT = {
  surveyDays:
    'Dates with survey hours on this aircraft’s flight entries. Ferry, comp box and calibration days are not survey days.',
  projectDays:
    'Every calendar day the aircraft is on the job, within the range: from its first to its last daily entry on the project, flown or not (standby, maintenance and travel days count).',
  /** The total row's Project days (decision 548). */
  totalProjectDays:
    'Calendar days with any of these aircraft on the job, each day counted once: two aircraft on the job for the same 9 days give 9.',
  hoursPerDay:
    'Flight hours ÷ project days: survey, comp box, calibration and ferry hours spread over every day on the job.',
} as const;

/** The Survey days tile's hover text (decision 548: explained as Project days is). */
export const SURVEY_DAYS_TILE_HINT =
  'Calendar days in the range with survey hours on any flight entry, each day counted once. Ferry, comp box and calibration days are not survey days.';

/** The viewer's local date as `YYYY-MM-DD`. */
export function localToday(now: Date = new Date()): string {
  const p = (n: number): string => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${p(now.getMonth() + 1)}-${p(now.getDate())}`;
}
